import {
  animate,
  state,
  style,
  transition,
  trigger
} from '@angular/animations';
import { Directionality } from '@angular/cdk/bidi';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow
} from '@angular/material/table';
import { AppStateService } from '@pcplus/core';
import { SearchService } from '@pcplus/infrastructure';
import { PermissionsService, Permission } from '@pcplus/security';
import { combineLatest, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  take,
  takeUntil
} from 'rxjs/operators';
import { ProtectedResourceInfo } from '../../../dto/protected-resource-info';
import {
  ProtectedResources,
  SafeMFASystem
} from '../../../dto/security-identifier';
import { MfaService } from '../../../services/mfa.service';
import { AddResourceData } from '../add-resource/add-resource-data';
import { AddResourceComponent } from '../add-resource/add-resource.component';
import { NgIf, NgClass, AsyncPipe, DatePipe } from '@angular/common';
import { MatFabButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { MatBadge } from '@angular/material/badge';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'mfa-protected-resources',
  templateUrl: './protected-resources.component.html',
  styleUrls: ['./protected-resources.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed, void', style({ height: '0px', overflow: 'hidden' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
      transition(
        'expanded <=> void',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      )
    ])
  ],
  imports: [
    NgIf,
    MatFabButton,
    MatTooltip,
    MatIcon,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    NgClass,
    ExtendedModule,
    MatBadge,
    MatSortHeader,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    RouterLink,
    AsyncPipe,
    DatePipe
  ]
})
export class ProtectedResourcesComponent implements OnInit, OnDestroy {
  constructor(
    private search: SearchService,
    public mfa: MfaService,
    private stateService: AppStateService,
    private dir: Directionality,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    public permissions: PermissionsService
  ) {
    this.isRtl = dir.value === 'rtl';
    this.search
      .registerSearchClient('protected-resources')
      .pipe(
        takeUntil(this.onDestroy$),
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe((val) => {
        this.searchValue = val;
        this.refreshDisplayedResources();
      });
    combineLatest([permissions.selectedCustomer, this.refresh$])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(([customerId]) => {
        this.customerId = customerId;
        if (this.customerId) {
          this.mfa
            .getProtectedResources(this.customerId)
            .pipe(take(1))
            .subscribe((r) => {
              this.resources = r;
              this.refreshDisplayedResources();
            });
        } else {
          this.resources = [];
          this.refreshDisplayedResources();
        }
      });
    this.refresh$.next();
  }

  private searchValue: string | null = null;
  private onDestroy$ = new Subject<void>();
  private customerId: string | null = null;
  private refresh$ = new Subject<void>();
  permission = Permission;
  @ViewChild(MatSort, { static: true })
  sort: MatSort | null = null;
  activity = [SafeMFASystem, ProtectedResources];
  resources: ProtectedResourceInfo[] | null = null;
  displayedResources: MatTableDataSource<ProtectedResourceInfo> | null = null;
  isRtl: boolean;
  highlightedResource: ProtectedResourceInfo | null = null;

  ngOnInit() {
    this.stateService.updateCurrentModuleName($localize`Protected Resources`);
  }

  refreshDisplayedResources() {
    const lowered = this.searchValue?.toLowerCase();
    this.displayedResources = new MatTableDataSource<ProtectedResourceInfo>(
      this.searchValue
        ? (this.resources?.filter((r) =>
            r.name.toLowerCase().includes(lowered!)
          ) ?? undefined)
        : (this.resources?.slice() ?? undefined)
    );
    this.displayedResources.sort = this.sort;
    if (this.highlightedResource) {
      this.highlightedResource =
        this.resources?.find((r) => r.id === this.highlightedResource?.id) ??
        null;
    }
  }

  getIconTooltip(resource: ProtectedResourceInfo) {
    return resource.enableLoginCache ? $localize`Local Cache Enabled` : '';
  }

  addResource() {
    this.dialog
      .open(AddResourceComponent, { direction: this.dir.value })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result: AddResourceData) => {
        if (result && this.customerId) {
          this.mfa
            .addResource(this.customerId, result.name, result.resourceType)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => {
              this.snackBar.open(
                $localize`The resource has been added`,
                $localize`OK`,
                {
                  duration: 2000
                }
              );
              this.refresh$.next();
            });
        }
      });
  }

  handleUsersClick(resource: ProtectedResourceInfo, event: MouseEvent) {
    resource.usersExpanded = !resource.usersExpanded;
    event.preventDefault();
    event.stopPropagation();
    return false;
  }

  handlePendingClick(resource: ProtectedResourceInfo, event: MouseEvent) {
    resource.pendingExpanded = !resource.pendingExpanded;
    event.preventDefault();
    event.stopPropagation();
    return false;
  }

  ngOnDestroy() {
    this.onDestroy$.next();
  }
}
